// @ts-nocheck
import { isDesktop, isTablet } from '@theme/utilities';

(() => {
  const STORAGE_KEY = 'recently_viewed_products_v1';
  const productCache = new Map();

  const normalizeHandle = (value) => {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getExcludedProductHandles = () => {
    const handles = window.__mfExcludedProductHandles__;
    if (!Array.isArray(handles)) return [];
    return handles.map((handle) => normalizeHandle(handle)).filter(Boolean);
  };

  const getProductHandleFromUrl = (url) => {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const segments = parsedUrl.pathname.split('/').filter(Boolean);
      return normalizeHandle(segments[segments.length - 1] || '');
    } catch (error) {
      return normalizeHandle(url.split('/').filter(Boolean).pop() || '');
    }
  };

  const isExcludedProduct = (product) => {
    const productHandle = getProductHandleFromUrl(product?.url);
    if (!productHandle) return false;
    return getExcludedProductHandles().includes(productHandle);
  };

  const getRecentlyViewed = () => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];

      const filtered = parsed.filter((product) => !isExcludedProduct(product));
      if (filtered.length !== parsed.length) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      }

      return filtered;
    } catch (e) {
      return [];
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return price;
  };

  const escapeHtml = (value) => {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  };

  const truncateText = (value, maxLength) => {
    const text = String(value || '').trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength).trim()}...`;
  };

  const getCurrencyCode = () => {
    return window.Shopify?.currency?.active || 'USD';
  };

  const formatMoneyValue = (cents) => {
    const numericCents = Number(cents);
    if (Number.isNaN(numericCents)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: getCurrencyCode(),
      }).format(numericCents / 100);
    } catch (error) {
      return (numericCents / 100).toFixed(2);
    }
  };

  const PRODUCT_CARD_IMAGE_WIDTH = 1080;
  const PRODUCT_CARD_IMAGE_WIDTHS = [360, 540, 720, 900, 1080];
  const PRODUCT_CARD_IMAGE_SIZES =
    '(min-width: 1280px) calc((100vw - 8rem) / 4), (min-width: 880px) calc((100vw - 6rem) / 4), (min-width: 640px) calc((100vw - 4rem) / 3), calc((100vw - 3rem) / 1.5)';

  const buildResponsiveImageUrl = (
    imageUrl,
    width = PRODUCT_CARD_IMAGE_WIDTH
  ) => {
    if (!imageUrl) return '';

    try {
      const url = new URL(imageUrl, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return imageUrl;
    }
  };

  const buildResponsiveImageSrcSet = (imageUrl) => {
    if (!imageUrl) return '';

    return PRODUCT_CARD_IMAGE_WIDTHS.map((width) => {
      return `${buildResponsiveImageUrl(imageUrl, width)} ${width}w`;
    }).join(', ');
  };

  const applyResponsiveProductImage = (imageNode, imageUrl, altText) => {
    if (!(imageNode instanceof HTMLImageElement) || !imageUrl) return;

    imageNode.src = buildResponsiveImageUrl(imageUrl);
    imageNode.srcset = buildResponsiveImageSrcSet(imageUrl);
    imageNode.sizes = PRODUCT_CARD_IMAGE_SIZES;
    imageNode.alt = altText;
    imageNode.loading = 'lazy';
    imageNode.decoding = 'async';
  };

  const normalizeOptionValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      return String(value.name || value.value || value.label || '');
    }
    return String(value);
  };

  const getVariantOptions = (variant, optionCount) => {
    return Array.from({ length: optionCount }, (_, index) => {
      return normalizeOptionValue(variant[`option${index + 1}`]);
    });
  };

  const normalizeProductData = (product, item) => {
    const optionNames = Array.isArray(product.options)
      ? product.options.map((option) => normalizeOptionValue(option))
      : [];
    const variantOptionCount =
      optionNames.length ||
      (Array.isArray(product.variants) &&
        product.variants[0]?.options?.length) ||
      0;
    const variants = Array.isArray(product.variants)
      ? product.variants.map((variant) => ({
          ...variant,
          options: getVariantOptions(variant, variantOptionCount),
        }))
      : [];
    const selectedVariant =
      variants.find((variant) => variant.available) || variants[0] || null;

    return {
      id: String(product.id || item.id || ''),
      title: product.title || item.title || 'Product',
      url: item.url || product.url || '#',
      available: Boolean(product.available),
      description: truncateText(
        String(product.description || '').replace(/<[^>]+>/g, ' '),
        100
      ),
      featuredImage:
        product.featured_image ||
        (Array.isArray(product.images) ? product.images[0] : '') ||
        item.image ||
        '',
      variants,
      optionNames,
      hasOnlyDefaultVariant:
        variants.length <= 1 && (!optionNames[0] || optionNames[0] === 'Title'),
      selectedVariant,
      price_min: Number(product.price_min ?? product.price ?? 0),
      price_max: Number(product.price_max ?? product.price ?? 0),
      price_varies: Boolean(product.price_varies),
    };
  };

  const getProductJsonUrl = (productUrl) => {
    const url = new URL(productUrl || '#', window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.toString();
  };

  const fetchProductData = async (item) => {
    const cacheKey = item.url || item.id || item.title || '';
    if (!cacheKey) return null;

    if (productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    const request = fetch(getProductJsonUrl(item.url), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load product data');
        }
        return response.json();
      })
      .then((product) => normalizeProductData(product, item))
      .catch(() => null);

    productCache.set(cacheKey, request);
    return request;
  };

  const getBlockConfig = (blockRoot) => {
    return {
      showWishlist: blockRoot.dataset.showWishlist === 'true',
      showQuickView: blockRoot.dataset.showQuickview === 'true',
      showAddToCart: blockRoot.dataset.showAddToCart === 'true',
      quickViewLabel: blockRoot.dataset.labelQuickView || 'Quick view',
      selectOptionsLabel:
        blockRoot.dataset.labelSelectOptions || 'Select options',
      addToCartLabel: blockRoot.dataset.labelAddToCart || 'Add to cart',
      addingToCartLabel:
        blockRoot.dataset.labelAddingToCart || 'Adding to cart...',
      addedToCartMessage:
        blockRoot.dataset.messageAddedToCart || 'Added to cart',
      addToCartErrorMessage:
        blockRoot.dataset.messageAddToCartError ||
        'Unable to add this item right now.',
      decreaseQuantityLabel:
        blockRoot.dataset.labelDecreaseQuantity || 'Decrease quantity',
      increaseQuantityLabel:
        blockRoot.dataset.labelIncreaseQuantity || 'Increase quantity',
      closeDialogLabel: blockRoot.dataset.labelCloseDialog || 'Close dialog',
      unavailableVariantLabel:
        blockRoot.dataset.labelUnavailableVariant ||
        'This variant is currently unavailable',
    };
  };

  const buildPriceMarkup = (product, currentClass = '', compareClass = '') => {
    if (!product) return '';

    const displayMode = Theme.settings?.productCardPriceDisplay || 'range';
    const priceMin = Number(
      product.price_min ?? product.selectedVariant?.price ?? 0
    );
    const priceMax = Number(
      product.price_max ?? product.selectedVariant?.price ?? 0
    );
    const priceVaries = Boolean(product.price_varies);

    if (displayMode === 'max') {
      return `<span class="product-price__current ${currentClass}">${escapeHtml(formatMoneyValue(priceMax))}</span>`;
    }

    if (displayMode === 'range' && priceVaries) {
      return `<span class="product-price__current ${currentClass}"><span class="product-price__amount product-price__amount--min">${escapeHtml(formatMoneyValue(priceMin))}</span><span class="product-price__amount product-price__amount--max">${escapeHtml(formatMoneyValue(priceMax))}</span></span>`;
    }

    return `<span class="product-price__current ${currentClass}">${escapeHtml(formatMoneyValue(priceMin))}</span>`;
  };

  const stringifyScriptJson = (value) => {
    return JSON.stringify(value).replaceAll('</script>', '<\\/script>');
  };

  const isColorOption = (optionName, value) => {
    const normalizedName = String(optionName || '').toLowerCase();
    return (
      normalizedName.includes('color') ||
      normalizedName.includes('colour') ||
      String(value || '').includes('#')
    );
  };

  const resolveMappedSwatchColor = (value) => {
    const mapping = window.Theme?.settings?.customColorMapping;
    if (!Array.isArray(mapping)) return '';

    const normalizedValue = String(value || '')
      .trim()
      .toLowerCase();
    const match = mapping.find(
      (entry) =>
        String(entry?.name || '')
          .trim()
          .toLowerCase() === normalizedValue
    );

    return match?.hex ? String(match.hex) : '';
  };

  const buildVariantValueMarkup = (optionName, value) => {
    if (!isColorOption(optionName, value)) {
      return `<span class="product-variant-label">${escapeHtml(value)}</span>`;
    }

    const mappedColor = resolveMappedSwatchColor(value);
    if (mappedColor) {
      return `<span class="product-variant-swatch" style="background-color: ${escapeHtml(mappedColor)};" aria-hidden="true"></span><span class="visually-hidden">${escapeHtml(value)}</span>`;
    }

    return `<span class="product-variant-label">${escapeHtml(value)}</span>`;
  };

  const buildVariantsMarkup = (product, config) => {
    if (product.hasOnlyDefaultVariant) return '';

    const variantsJson = stringifyScriptJson(product.variants);
    const pricesById = Object.fromEntries(
      product.variants.map((variant) => [
        String(variant.id),
        {
          price: formatMoneyValue(variant.price),
          price_value: variant.price,
          compare_at_price:
            variant.compare_at_price && variant.compare_at_price > variant.price
              ? formatMoneyValue(variant.compare_at_price)
              : null,
          compare_at_price_value:
            variant.compare_at_price && variant.compare_at_price > variant.price
              ? variant.compare_at_price
              : null,
        },
      ])
    );

    const optionsMarkup = product.optionNames
      .map((optionName, optionIndex) => {
        const values = [
          ...new Set(
            product.variants
              .map((variant) =>
                normalizeOptionValue(variant.options[optionIndex])
              )
              .filter((value) => value !== '')
          ),
        ];

        const valueButtons = values
          .map((value) => {
            const matchingAvailableVariant = product.variants.find(
              (variant) =>
                variant.available &&
                String(variant.options[optionIndex] || '') === value
            );
            const matchingVariant =
              matchingAvailableVariant ||
              product.variants.find(
                (variant) => variant.options[optionIndex] === value
              );
            const isDisabled = !matchingAvailableVariant;
            const isActive =
              String(product.selectedVariant?.options[optionIndex] || '') ===
              value;

            return `<button
              type="button"
              class="product-variant-button${isActive ? ' is-active' : ''}${isDisabled ? ' is-disabled' : ''}"
              ${isDisabled ? 'disabled aria-disabled="true"' : ''}
              data-variant-id="${escapeHtml(matchingVariant?.id || '')}"
              data-option-value="${escapeHtml(value)}"
              aria-pressed="${isActive ? 'true' : 'false'}"
              title="${escapeHtml(value)}"
            >
              ${buildVariantValueMarkup(optionName, value)}
            </button>`;
          })
          .join('');

        const variantHeadingClass =
          'product-variant-heading' +
          (Theme.settings?.quickViewVariantUseGlobalH3 !== false
            ? ' text-heading-h3'
            : '');

        return `<div class="product-variants__option" data-option-index="${optionIndex}">
          <h3 class="${variantHeadingClass}">${escapeHtml(optionName)}</h3>
          <div class="product-variants__values">${valueButtons}</div>
        </div>`;
      })
      .join('');

    return `<product-variants class="product-variants" data-product-id="${escapeHtml(product.id)}">
      <input
        type="hidden"
        name="id"
        value="${escapeHtml(product.selectedVariant?.id || '')}"
        class="product-variants__input"
      >
      <script type="application/json" class="product-variants__variants-json">${variantsJson}</script>
      <script type="application/json" class="product-variants__prices-json">${stringifyScriptJson(
        pricesById
      )}</script>
      ${optionsMarkup}
      <p
        class="product-variants__status"
        data-unavailable-message="${escapeHtml(config.unavailableVariantLabel)}"
        hidden
      ></p>
    </product-variants>`;
  };

  const buildQuantityControlsMarkup = (product, config) => {
    return `<quantity-selector data-product-id="${escapeHtml(product.id)}" data-min="1" data-step="1">
      <div class="quantity-controls" data-quantity-selector>
        <button
          class="button-variant-outline button-size-icon-md quantity-controls__btn quantity-controls__btn--decrease"
          aria-label="${escapeHtml(config.decreaseQuantityLabel)}"
          type="button"
          data-quantity-decrease
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-minus" aria-hidden="true">
            <path d="M5 12h14"></path>
          </svg>
        </button>
        <span class="quantity-controls__value" data-quantity-value>1</span>
        <button
          class="button-variant-outline button-size-icon-md quantity-controls__btn quantity-controls__btn--increase"
          aria-label="${escapeHtml(config.increaseQuantityLabel)}"
          type="button"
          data-quantity-increase
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-plus" aria-hidden="true">
            <path d="M5 12h14"></path>
            <path d="M12 5v14"></path>
          </svg>
        </button>
      </div>
      <input type="hidden" name="quantity" value="1" data-quantity-input>
    </quantity-selector>`;
  };

  const buildAddToCartButtonMarkup = (
    config,
    buttonClass,
    buttonVariantClass,
    buttonSizeClass = 'button-size-default',
    iconOnly = false
  ) => {
    const labelMarkup = iconOnly
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="product-card__mobile-action-icon" aria-hidden="true"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.72a2 2 0 0 0 2-1.58L23 6H6"></path></svg><span class="visually-hidden" data-add-to-cart-label>${escapeHtml(config.addToCartLabel)}</span>`
      : escapeHtml(config.addToCartLabel);

    return `<button
      type="submit"
      class="add-to-cart-button button ${buttonVariantClass} ${buttonSizeClass} ${buttonClass}"
      data-add-to-cart-submit
      data-label-default="${escapeHtml(config.addToCartLabel)}"
      data-label-loading="${escapeHtml(config.addingToCartLabel)}"
      data-message-success="${escapeHtml(config.addedToCartMessage)}"
      data-message-error="${escapeHtml(config.addToCartErrorMessage)}"
      aria-label="${escapeHtml(config.addToCartLabel)} ${escapeHtml(buttonClass.includes('product-card__mobile-action-button') ? '' : '')}"
    >
      ${labelMarkup}
    </button>`;
  };

  const buildWishlistButtonMarkup = (product, wishlisted = false) => {
    const productTitle = product.title || 'Product';
    const productPrice = product.selectedVariant
      ? formatMoneyValue(product.selectedVariant.price)
      : '';
    const addLabel = `Add ${productTitle} to wishlist`;
    const removeLabel = `Remove ${productTitle} from wishlist`;

    return `<div class="wishlist-button-wrapper" title="Toggle ${escapeHtml(productTitle)}">
      <button
        type="button"
        class="wishlist-button${wishlisted ? ' is-wishlisted' : ''}"
        data-action="toggle-wishlist"
        data-product-id="${escapeHtml(product.id)}"
        data-product-title="${escapeHtml(productTitle)}"
        data-product-image="${escapeHtml(product.featuredImage || '')}"
        data-wishlist-product-price="${escapeHtml(productPrice)}"
        data-product-url="${escapeHtml(product.url || '#')}"
        data-label-add="${escapeHtml(addLabel)}"
        data-label-remove="${escapeHtml(removeLabel)}"
        aria-label="${escapeHtml(wishlisted ? removeLabel : addLabel)}"
        aria-pressed="${wishlisted ? 'true' : 'false'}"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="wishlist-button__icon" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </button>
    </div>`;
  };

  const buildQuickViewIconButtonMarkup = (product, config) => {
    return `<div class="quick-view-button-wrapper quick-view-button-wrapper--icon" title="${escapeHtml(config.quickViewLabel)} ${escapeHtml(product.title)}">
      <button
        type="button"
        class="quick-view-button quick-view-button--icon"
        data-quick-view-trigger
        data-product-id="${escapeHtml(product.id)}"
        aria-haspopup="dialog"
        aria-label="${escapeHtml(config.quickViewLabel)} ${escapeHtml(product.title)}"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="quick-view-button__icon" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"></path><circle cx="12" cy="12" r="3"></circle></svg>
      </button>
    </div>`;
  };

  const buildQuickViewTextButtonMarkup = (
    product,
    config,
    label,
    buttonClass
  ) => {
    return `<div class="quick-view-button-wrapper quick-view-button-wrapper--text" title="${escapeHtml(config.quickViewLabel)} ${escapeHtml(product.title)}">
      <button
        type="button"
        class="quick-view-button quick-view-button--text ${buttonClass}"
        data-quick-view-trigger
        data-product-id="${escapeHtml(product.id)}"
        aria-haspopup="dialog"
        aria-label="${escapeHtml(config.quickViewLabel)} ${escapeHtml(product.title)}"
      >
        <span class="quick-view-button__label">${escapeHtml(label)}</span>
      </button>
    </div>`;
  };

  const buildCardCtaMarkup = (product, config) => {
    if (!config.showAddToCart || !product.available) return '';

    if (!product.hasOnlyDefaultVariant) {
      return buildQuickViewTextButtonMarkup(
        product,
        config,
        config.selectOptionsLabel,
        'product-card__cta-button button button-variant-primary button-size-default'
      );
    }

    if (!product.selectedVariant) return '';

    return `<form>
      <input type="hidden" name="id" value="${escapeHtml(product.selectedVariant.id)}">
      <input type="hidden" name="quantity" value="1">
      ${buildAddToCartButtonMarkup(
        config,
        'product-card__cta-button',
        'button-variant-default'
      )}
    </form>`;
  };

  const buildMobileCtaMarkup = (product, config) => {
    if (!config.showAddToCart || !product.available) return '';

    if (!product.hasOnlyDefaultVariant) {
      return `<dialog-trigger id="quick-view-${escapeHtml(product.id)}" class="product-card__mobile-action">
        <button
          type="button"
          class="product-card__mobile-action-button button button-variant-default button-size-icon-sm"
          data-quick-view-trigger
          data-product-id="${escapeHtml(product.id)}"
          aria-haspopup="dialog"
          aria-label="${escapeHtml(config.selectOptionsLabel)} ${escapeHtml(product.title)}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="product-card__mobile-action-icon" aria-hidden="true"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.72a2 2 0 0 0 2-1.58L23 6H6"></path></svg>
          <span class="visually-hidden">${escapeHtml(config.selectOptionsLabel)}</span>
        </button>
      </dialog-trigger>`;
    }

    if (!product.selectedVariant) return '';

    return `<form>
      <input type="hidden" name="id" value="${escapeHtml(product.selectedVariant.id)}">
      <input type="hidden" name="quantity" value="1">
      ${buildAddToCartButtonMarkup(
        config,
        'product-card__mobile-action-button',
        'button-variant-default',
        'button-size-icon-sm',
        true
      )}
    </form>`;
  };

  const buildQuickViewVariantHeadingStyle = () => {
    if (Theme.settings?.quickViewVariantUseGlobalH3 !== false) {
      return '';
    }

    const sizes = Theme.settings?.quickViewVariantHeadingSizes;
    if (!sizes) {
      return '';
    }

    return ` style="--quick-view-variant-heading-size-mobile: ${sizes.mobile}px; --quick-view-variant-heading-size-tablet: ${sizes.tablet}px; --quick-view-variant-heading-size-desktop: ${sizes.desktop}px;"`;
  };

  const buildQuickViewTemplateMarkup = (product, config) => {
    const imageMarkup = product.featuredImage
      ? `<div class="quick-view-images"><img src="${escapeHtml(product.featuredImage)}" alt="${escapeHtml(product.title)}" loading="lazy"></div>`
      : '';
    const variantsMarkup = buildVariantsMarkup(product, config);
    const defaultVariantId = product.selectedVariant?.id || '';
    const formIdMarkup = product.hasOnlyDefaultVariant
      ? `<input type="hidden" name="id" value="${escapeHtml(defaultVariantId)}">`
      : '';

    return `<div class="quick-view-template"${buildQuickViewVariantHeadingStyle()}>
      <button
        type="button"
        class="quick-view-close"
        data-dialog-close
        aria-label="${escapeHtml(config.closeDialogLabel)}"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="quick-view-close__icon" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
      ${imageMarkup}
      <div class="quick-view-content">
        <div class="quick-view-header">
          <h2>${escapeHtml(product.title)}</h2>
        </div>
        <div class="quick-view-description">${escapeHtml(product.description)}</div>
        <div class="quick-view-form">
          <form>
            ${variantsMarkup}
            ${formIdMarkup}
            <div class="product-variants__price">
              <div class="quick-view-price">${buildPriceMarkup(
                product,
                'quick-view-price__current',
                'quick-view-price__compare'
              )}</div>
            </div>
            <div class="quick-view-quantity">
              ${buildQuantityControlsMarkup(product, config)}
            </div>
            <div class="quick-view-buttons">
              <div>
                ${buildAddToCartButtonMarkup(config, '', 'button-variant-outline')}
              </div>
              <div>
                ${buildWishlistButtonMarkup(product)}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>`;
  };

  const syncWishlistButtons = (root, product, wishlisted) => {
    root
      .querySelectorAll('[data-action="toggle-wishlist"]')
      .forEach((button) => {
        if (!(button instanceof HTMLElement)) return;

        const title = product.title || 'Product';
        const addLabel = `Add ${title} to wishlist`;
        const removeLabel = `Remove ${title} from wishlist`;

        button.dataset.productId = product.id || '';
        button.dataset.productTitle = title;
        button.dataset.productImage = product.featuredImage || '';
        button.dataset.wishlistProductPrice = product.selectedVariant
          ? formatMoneyValue(product.selectedVariant.price)
          : '';
        button.dataset.productUrl = product.url || '#';
        button.dataset.labelAdd = addLabel;
        button.dataset.labelRemove = removeLabel;
        button.setAttribute('aria-label', wishlisted ? removeLabel : addLabel);
        button.setAttribute('aria-pressed', wishlisted ? 'true' : 'false');
        button.classList.toggle('is-wishlisted', wishlisted);

        const wrapper = button.closest('.wishlist-button-wrapper');
        if (wrapper instanceof HTMLElement) {
          wrapper.title = `Toggle ${title}`;
        }
      });
  };

  const syncQuickViewButtons = (root, product, config) => {
    root.querySelectorAll('.quick-view-button').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.dataset.productId = product.id;
      button.setAttribute(
        'aria-label',
        `${config.quickViewLabel} ${product.title}`
      );
    });

    root.querySelectorAll('.quick-view-button-wrapper').forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      wrapper.title = `${config.quickViewLabel} ${product.title}`;
    });
  };

  const syncQuickViewDialog = (itemRoot, product, config) => {
    const needsQuickView =
      config.showQuickView ||
      (config.showAddToCart &&
        product.available &&
        !product.hasOnlyDefaultVariant);
    let dialogContent = itemRoot.querySelector(
      'dialog-content[id^="quick-view-"]'
    );

    if (!needsQuickView) {
      if (dialogContent instanceof HTMLElement) {
        dialogContent.remove();
      }
      return;
    }

    if (!(dialogContent instanceof HTMLElement)) {
      dialogContent = document.createElement('dialog-content');
      itemRoot.appendChild(dialogContent);
    }

    dialogContent.id = `quick-view-${product.id}`;
    dialogContent.className = 'quick-view-template';
    dialogContent.setAttribute('data-dialog-class', 'quick-view-dialog');
    dialogContent.innerHTML = `<template>${buildQuickViewTemplateMarkup(
      product,
      config
    )}</template>`;
  };

  const syncCallToAction = (itemRoot, product, config) => {
    const ctaRoot = itemRoot.querySelector('.product-card__call-to-action');
    if (!(ctaRoot instanceof HTMLElement)) return;

    if (!config.showAddToCart || !product.available) {
      ctaRoot.innerHTML = '';
      return;
    }

    ctaRoot.innerHTML = buildCardCtaMarkup(product, config);
  };

  const syncMobileCallToAction = (itemRoot, product, config) => {
    const mobileCtaRoot = itemRoot.querySelector(
      '[data-recently-viewed-mobile-cta]'
    );
    if (!(mobileCtaRoot instanceof HTMLElement)) return;

    if (!config.showAddToCart || !product.available) {
      mobileCtaRoot.innerHTML = '';
      return;
    }

    mobileCtaRoot.innerHTML = buildMobileCtaMarkup(product, config);
  };

  const removeNodes = (root, selectors) => {
    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((node) => node.remove());
    });
  };

  const clearRenderedCards = (root) => {
    root
      .querySelectorAll('[data-recently-viewed-rendered="true"]')
      .forEach((node) => node.remove());
  };

  const createCard = async (blockRoot, item) => {
    const template = blockRoot.querySelector(
      '[data-recently-viewed-card-template]'
    );
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = template.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment)) return null;

    const itemRoot = fragment.firstElementChild;
    if (!(itemRoot instanceof HTMLElement)) return null;

    const card = itemRoot.querySelector('.product-card');
    if (!(card instanceof HTMLElement)) return null;

    const config = getBlockConfig(blockRoot);
    const product = await fetchProductData(item);
    const resolvedProduct = product || {
      id: item.id || '',
      title: item.title || 'Product',
      url: item.url || '#',
      featuredImage: item.image || '',
      selectedVariant: null,
      available: false,
      hasOnlyDefaultVariant: true,
    };
    const title = resolvedProduct.title || 'Product';
    const imageUrl = resolvedProduct.featuredImage || item.image || '';
    const productUrl = resolvedProduct.url || item.url || '#';
    const price = product
      ? buildPriceMarkup(resolvedProduct)
      : formatPrice(item.price || '');
    const wishlisted = false;

    card.setAttribute('data-product-id', item.id || '');
    // Client-rendered cards miss the page-load IntersectionObserver that
    // adds animate-in; without it they stay opacity: 0 under .js-enabled.
    card.classList.add('animate-in');
    card.setAttribute('data-pc-animated', 'true');

    itemRoot.querySelectorAll('.product-card--link').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      link.href = productUrl;
      link.title = title;
    });

    itemRoot.querySelectorAll('.product-card-title').forEach((heading) => {
      heading.textContent = title;
      if (heading instanceof HTMLElement && item.id) {
        heading.id = `recently-viewed-product-title-${item.id}`;
      }
    });

    itemRoot.querySelectorAll('.product-card__price').forEach((priceNode) => {
      if (!(priceNode instanceof HTMLElement)) return;
      priceNode.innerHTML = price;
    });

    syncWishlistButtons(itemRoot, resolvedProduct, wishlisted);

    if (product) {
      card.setAttribute('data-product-id', resolvedProduct.id);
      syncQuickViewButtons(itemRoot, resolvedProduct, config);
      syncCallToAction(itemRoot, resolvedProduct, config);
      syncMobileCallToAction(itemRoot, resolvedProduct, config);
      syncQuickViewDialog(itemRoot, resolvedProduct, config);
    } else {
      removeNodes(itemRoot, ['.quick-view-button-wrapper', 'dialog-content']);
      const ctaRoot = itemRoot.querySelector('.product-card__call-to-action');
      if (ctaRoot instanceof HTMLElement) {
        ctaRoot.innerHTML = '';
      }
      const mobileCtaRoot = itemRoot.querySelector(
        '[data-recently-viewed-mobile-cta]'
      );
      if (mobileCtaRoot instanceof HTMLElement) {
        mobileCtaRoot.innerHTML = '';
      }
    }

    itemRoot
      .querySelectorAll('.product-card__image-primary')
      .forEach((imageNode) => {
        if (imageNode instanceof HTMLImageElement) {
          if (!imageUrl) {
            imageNode.remove();
            return;
          }

          applyResponsiveProductImage(imageNode, imageUrl, title);
        }
      });

    if (imageUrl) {
      const placeholder = itemRoot.querySelector('.product-card__placeholder');
      if (placeholder instanceof HTMLElement) {
        const image = document.createElement('img');
        image.className = 'image--fit-cover product-card__image-primary';
        applyResponsiveProductImage(image, imageUrl, title);
        placeholder.replaceWith(image);
      }
    }

    return itemRoot;
  };

  const insertSkeletons = (blockRoot, count) => {
    const mobileGrid = blockRoot.querySelector(
      '[data-recently-viewed-grid-mobile]'
    );
    const desktopGrid = blockRoot.querySelector(
      '[data-recently-viewed-grid-desktop]'
    );
    // The data attribute may be applied to the scroller itself or to a
    // wrapper that contains the scroller. Accept either case.
    const mobileScroller = mobileGrid
      ? mobileGrid.classList && mobileGrid.classList.contains('hs-scroll')
        ? mobileGrid
        : mobileGrid.querySelector('.hs-scroll')
      : null;
    const desktopContainer = desktopGrid || null;
    const skeletonTemplate = blockRoot.querySelector(
      '[data-recently-viewed-skeleton-template]'
    );
    if (!skeletonTemplate) return;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const clone = skeletonTemplate.content.cloneNode(true);
      const el = clone.firstElementChild;
      if (el instanceof HTMLElement) el.dataset.recentlyViewedSkeleton = 'true';
      frag.appendChild(clone);
    }
    if (mobileScroller) mobileScroller.appendChild(frag.cloneNode(true));
    if (desktopContainer) desktopContainer.appendChild(frag);
  };

  const removeSkeletons = (blockRoot) => {
    blockRoot
      .querySelectorAll('[data-recently-viewed-skeleton="true"]')
      .forEach((n) => n.remove());
  };

  /**
   * @param {HTMLElement} blockRoot
   * @param {boolean} isEmpty
   */
  const syncSectionVisibility = (blockRoot, isEmpty) => {
    if (blockRoot.dataset.hideWhenEmpty !== 'true') return;

    const sectionEl = blockRoot.closest('[data-recently-viewed-section]');
    if (!(sectionEl instanceof HTMLElement)) return;

    if (isEmpty) {
      sectionEl.setAttribute('hidden', '');
      return;
    }

    sectionEl.removeAttribute('hidden');
  };

  const renderBlock = async (blockRoot) => {
    const mobileGrid = blockRoot.querySelector(
      '[data-recently-viewed-grid-mobile]'
    );
    const desktopGrid = blockRoot.querySelector(
      '[data-recently-viewed-grid-desktop]'
    );
    if (!mobileGrid && !desktopGrid) return;

    // show skeletons immediately while we read storage
    const cs = getComputedStyle(blockRoot);
    const mobileCols =
      Number(cs.getPropertyValue('--recently-viewed-columns')) || 1;
    const tabletCols =
      Number(cs.getPropertyValue('--recently-viewed-tablet-columns')) ||
      mobileCols;
    const desktopCols =
      Number(cs.getPropertyValue('--recently-viewed-desktop-columns')) ||
      tabletCols;
    const tabletRows =
      Number(cs.getPropertyValue('--recently-viewed-tablet-rows')) || 1;
    const desktopRows =
      Number(cs.getPropertyValue('--recently-viewed-desktop-rows')) || 1;

    const mobileLimit = Math.max(1, Math.floor(mobileCols));
    const tabletLimit = Math.max(
      1,
      Math.floor(tabletCols) * Math.max(1, Math.floor(tabletRows))
    );
    const desktopLimit = Math.max(
      1,
      Math.floor(desktopCols) * Math.max(1, Math.floor(desktopRows))
    );

    let maxCount = mobileLimit;
    try {
      if (isDesktop()) {
        maxCount = desktopLimit;
      } else if (isTablet()) {
        maxCount = tabletLimit;
      }
    } catch (e) {
      maxCount = mobileLimit;
    }

    insertSkeletons(blockRoot, maxCount);

    const items = getRecentlyViewed();
    const displayItems = items.slice(0, maxCount);

    const mobileEmpty = blockRoot.querySelector(
      '[data-recently-viewed-empty-mobile]'
    );
    const desktopEmpty = blockRoot.querySelector(
      '[data-recently-viewed-empty-desktop]'
    );
    const emptyNodes = [mobileEmpty, desktopEmpty].filter(Boolean);

    // Accept the data attribute being present on the scroller itself or on
    // a wrapper that contains the scroller.
    const mobileScroller = mobileGrid
      ? mobileGrid.classList && mobileGrid.classList.contains('hs-scroll')
        ? mobileGrid
        : mobileGrid.querySelector('.hs-scroll')
      : null;
    const desktopContainer = desktopGrid;

    if (mobileScroller) {
      clearRenderedCards(mobileScroller);
    }
    if (desktopContainer) {
      clearRenderedCards(desktopContainer);
    }

    // remove skeletons before rendering final state
    removeSkeletons(blockRoot);

    if (displayItems.length === 0) {
      emptyNodes.forEach((empty) => {
        empty.classList.add('is-visible');
      });
      syncSectionVisibility(blockRoot, true);
      return;
    }

    syncSectionVisibility(blockRoot, false);

    emptyNodes.forEach((empty) => {
      empty.classList.remove('is-visible');
    });

    const cards = await Promise.all(
      displayItems.map((item) => createCard(blockRoot, item))
    );

    cards.forEach((card) => {
      if (!card) return;

      if (mobileScroller) {
        const mobileCard = card.cloneNode(true);
        if (mobileCard instanceof HTMLElement) {
          mobileCard.dataset.recentlyViewedRendered = 'true';
        }
        mobileScroller.appendChild(mobileCard);
      }

      if (desktopContainer) {
        card.dataset.recentlyViewedRendered = 'true';
        desktopContainer.appendChild(card);
      }
    });

    if (
      (!mobileGrid || mobileGrid.childElementCount === 0) &&
      (!desktopGrid || desktopGrid.childElementCount === 0)
    ) {
      emptyNodes.forEach((empty) => {
        empty.classList.add('is-visible');
      });
    }
  };

  const initRecentlyViewedBlocks = () => {
    document.querySelectorAll('.recently-viewed-block').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      renderBlock(root).catch(() => {
        // Keep recently viewed rendering resilient when a product payload fails.
      });
    });
  };

  document.addEventListener('DOMContentLoaded', initRecentlyViewedBlocks);
  document.addEventListener(
    'recently-viewed:updated',
    initRecentlyViewedBlocks
  );
  document.addEventListener('shopify:section:load', initRecentlyViewedBlocks);
  document.addEventListener(
    'shopify:section:reorder',
    initRecentlyViewedBlocks
  );
  document.addEventListener('shopify:section:select', initRecentlyViewedBlocks);
  document.addEventListener('shopify:block:select', initRecentlyViewedBlocks);
})();
